import normalize, { Options as NormalizeOptions } from 'normalize-url'
import { parse as parseDomain } from 'tldts'
import { IMAGEKIT_URL_ENDPOINT, PROXY_BASE_URL } from './constants'
import { SHOP_DOMAIN_ALIAS_PREFIX_RE } from './urlStorage'

// NOTE: processSheetProducts/generateBulkId are intentionally NOT re-exported
// here. They pull in xlsx (~155KiB gzip) + papaparse + crypto-js, and this
// barrel is imported by dozens of browser components (getShopKey etc.).
// Import from '@dupe/common/helpers/processSheetProducts' directly — lazily
// (dynamic import) in client code.

/**
 * Minimum `effectiveVisualScore` a match needs to clear before the sort
 * treats it as a quality result. Matches below this floor are demoted
 * beneath any match above it under every sort criterion — picks, tier,
 * price, visual, and match — so a visually weak match can never sit
 * above a visually strong one regardless of the user's chosen ordering.
 * Catalog-quality concerns outweigh revenue-first placement, price
 * ordering, and raw position signals when similarity is obviously weak.
 *
 * 0.50 is the working threshold after observing real traffic under the
 * initial 0.45 cutoff (dining-table and lounge-chair queries still
 * surfaced borderline matches in the 46-49% band for "Best deals" and
 * "Best matches" sorts). Keep this as a shared constant so the server
 * sort and any client-side score view agree on the same boundary.
 */
export const LOW_QUALITY_VISUAL_FLOOR = 0.5

/**
 * Persistence schema version for the visual_matches cache. Bump whenever
 * the sort or scoring semantics change in a way that invalidates older
 * cached orderings — readers reject rows whose `sortVersion` differs from
 * this constant and force a fresh fan-out instead. Version history:
 *   1: pre-quality-gate; Recommended sort ignored visualScore entirely.
 *   2: adds the `LOW_QUALITY_VISUAL_FLOOR` gate to Recommended +
 *      RrfRecommended + Lens injection.
 */
export const VISUAL_MATCHES_SORT_VERSION = 2

/**
 * Whether `visualScore` is a real photo score. A Marqo score
 * (`visualScoreSource: 'marqo_image'`) of 0 is real: visualScore clamps
 * negative cosines to 0, so it means "these photos don't look alike", and a
 * failed embed leaves the score unset instead. An unmarked (pre-Marqo) score
 * of 0 was a silent-failure leak, so only a positive one counts.
 */
function hasUsableVisualScore(match: {
  visualScore?: number
  visualScoreSource?: string
}): match is { visualScore: number; visualScoreSource?: string } {
  if (typeof match.visualScore !== 'number') return false
  if (match.visualScoreSource === 'marqo_image') return match.visualScore >= 0
  return match.visualScore > 0
}

/**
 * Visual-match sort key used by `SortCriteria.VisualMatch` on both server
 * and client. Prefers the cross-source photo cosine (`visualScore`; Marqo-L
 * since DUP-3080, Pumice before) when it's populated AND non-zero; falls back
 * to the Turbopuffer catalog score (`1 - score`) for matches that came from
 * the vector catalog but didn't pick up a `visualScore` (most commonly
 * because the candidate photo embed failed or ran out of time). Matches with neither signal return
 * `-Infinity` so they sink to the end of a score-sorted list.
 *
 * Keeping this in a single helper prevents the server-side and
 * client-side VisualMatch sorts from drifting — they must agree or the
 * UI would show a different order than the API response.
 */
export function effectiveVisualScore(match: {
  visualScore?: number
  visualScoreSource?: string
  score?: number
  namespace?: string
  source_api?: string
}): number {
  // 1. Cross-source photo score if it's usable. See `hasUsableVisualScore`.
  if (hasUsableVisualScore(match)) return match.visualScore

  // 2. Turbopuffer catalog score (`$dist` inverted). Only trust it for
  //    actual Turbopuffer matches — Lens/Shopify `score` fields have
  //    different semantics.
  //
  //    Accept either `namespace` OR `source_api` tag because legacy
  //    cached rows (pre-namespace field) only carry `source_api`, and
  //    the scoring shortcut uses the same dual check. Keeping the two
  //    in sync prevents "scored fresh via shortcut but falls through to
  //    -Infinity on the read-side sort" inconsistencies.
  //
  //    Range guard is [0, 2] to cover the full cosine_distance scale
  //    configured on the `catalog_products` namespace. The prior
  //    `<= 1` cutoff silently rejected the (1, 2] tail — worst-
  //    similarity matches (similarity ≤ 0) fell to `-Infinity` and
  //    were treated as "unknown / above-floor" by `meetsVisualFloor`,
  //    letting the very worst TP rows skip the quality gate. Returning
  //    `1 - score` for those rows produces a non-positive similarity
  //    (e.g. $dist 1.5 → −0.5) which the floor check correctly
  //    classifies as below-floor.
  const isTurbopuffer =
    match.namespace === 'turbopuffer' || match.source_api === 'turbopuffer'
  if (
    isTurbopuffer &&
    typeof match.score === 'number' &&
    match.score >= 0 &&
    match.score <= 2
  ) {
    return 1 - match.score
  }
  return Number.NEGATIVE_INFINITY
}

/**
 * Synthesize an absolute quality estimate from a Google Lens match's
 * ordinal rank. Lens does not expose per-match similarity scores, only
 * position within the returned list — but that order itself is a
 * confidence signal. This helper projects rank → [0.3, 0.9] so Lens
 * matches can carry a numeric quality roughly comparable to
 * Turbopuffer's `1 - $dist` or PUMICE's `visualScore`, without
 * pretending we know more than we do (hence the floor at 0.3 and
 * ceiling at 0.9 — we never claim a Lens match is "certainly the best"
 * or "certainly terrible" without a real similarity measurement).
 *
 * Curve is linear, 1 step per rank:
 *   rank 1  → 0.90  (strong, competitive with TP $dist ~0.10)
 *   rank 5  → 0.70
 *   rank 10 → 0.45
 *   rank 13+ → 0.30 (floor; Lens tail is unreliable at this depth)
 *
 * ⛔ `rank` must be the rank among the Lens matches that SURVIVED
 * filtering, not the vendor's original `position`. Lens stamps
 * `position` before we drop excluded domains, cap brands and run
 * `filterVisualMatches`, and nothing renumbers the survivors — so the
 * third surviving tile can carry `position: 14` and land on the 0.3
 * floor. `rrfQualitySignal` receives the survivor rank from
 * `attachRrfScores` for exactly this reason.
 *
 * Anchored 2026-09-25 against judge grades (0–3) on the Fashion image
 * grids, where this sort is live: mean grade ≈ 2.86 × this weight
 * (R² 0.95, intercept ≈ 0), and the catalog leg's measured grade maps
 * to a weight of ~0.65 against the ~0.64 it is actually given. The 0.9
 * ceiling is therefore calibrated against survivor rank; leave it. See
 * docs/features/active/text-search-retrieval/findings-grid-scoring-terms-2026-09-26.md.
 */
export function lensQualityFromRank(rank: number): number {
  if (!Number.isFinite(rank) || rank < 1) return 0.3
  return Math.max(0.3, 0.9 - (rank - 1) * 0.05)
}

/**
 * Unified absolute-quality signal for RRF rank weighting. Returns a
 * value in [0, 1] where higher = more confidently a good match. Unlike
 * `effectiveVisualScore` (which returns `-Infinity` when no signal is
 * available — correct for a strict sort key), this helper always
 * returns a non-negative number so it can be multiplied into an RRF
 * rank contribution. It returns 0 only for a real Marqo photo score of 0
 * (photos not alike), which deliberately zeroes that row's contribution;
 * a missing score never does.
 *
 * Preference order:
 *   1. `visualScore` (photo cosine, cross-source absolute) when usable:
 *      >= 0 for a Marqo score, > 0 for an unmarked legacy one
 *   2. `1 - score` for Turbopuffer matches (catalog `$dist` inverted)
 *   3. `lensQualityFromRank(rankInSource ?? position)` for Lens matches
 *      (synthesized from ordinal rank — see `lensQualityFromRank`)
 *   4. `1` (full weight, no signal — preserves plain-RRF behavior)
 *
 * `rankInSource` is the 1-based rank of this match within its own
 * source's surviving list — the same rank the RRF contribution uses.
 * `attachRrfScores` always supplies it; `position` is only a fallback
 * for direct callers, and it is sparse for Lens (see
 * `lensQualityFromRank`).
 *
 * Used by the `qualityWeight` option on `attachRrfScores` and
 * `attachRrfScoresFromMerged`. See
 * docs/features/backlog/visual-match-quality/phase-5-rrf-plan.md.
 */
export function rrfQualitySignal(
  match: {
    visualScore?: number
    visualScoreSource?: string
    score?: number
    namespace?: string
    source_api?: string
    position?: number
  },
  rankInSource?: number
): number {
  if (hasUsableVisualScore(match)) return match.visualScore
  const isTurbopuffer =
    match.namespace === 'turbopuffer' || match.source_api === 'turbopuffer'
  if (
    isTurbopuffer &&
    typeof match.score === 'number' &&
    match.score >= 0 &&
    match.score <= 2
  ) {
    // Clamp at 0 so TP rows in the (1, 2] cosine-distance tail (worst
    // matches) don't contribute a negative number to the RRF weight —
    // `rrfQualitySignal`'s contract is non-negative, unlike the strict
    // `effectiveVisualScore` which legitimately returns signed values.
    return Math.max(0, 1 - match.score)
  }
  const isLens =
    match.source_api === 'google_lens' || match.source_api === 'serpapi_lens'
  const lensRank = rankInSource ?? match.position
  if (isLens && typeof lensRank === 'number' && lensRank >= 1) {
    return lensQualityFromRank(lensRank)
  }
  return 1
}

/**
 * Stable dedup identity for a visual match across sources.
 *
 * Different backends return the same product under different pids and
 * differently-formatted URLs (share params, affiliate wrappers, utm
 * tags). We need a coarse key that collapses those variants so RRF can
 * recognize cross-source consensus — e.g. "Safavieh Caralynn Bench" from
 * Google Lens is the same physical item as the Wayfair domain match
 * from Turbopuffer, and should combine its ranks into one entry.
 *
 * Strategy: normalize title (lowercased, whitespace-collapsed) + domain
 * of the link. Falls back to pid/link when title is missing.
 *
 * Good enough for v1; title variants across retailers ("Safavieh
 * Caralynn 62-inch Curved Waterfall Bench" vs "Safavieh Caralynn Curved
 * Waterfall Accent Bench 62\"") will not dedup — tolerable because
 * they'll both still rank via their own source's native signal.
 */
export function stableMatchId(match: {
  title?: string | null
  link?: string
  originalLink?: string
  shopKey?: string
  pid?: string
}): string {
  const title = (match.title ?? '').toLowerCase().replace(/\s+/g, ' ').trim()
  // Source-of-truth for retailer identity, in priority order:
  //   1. `originalLink` — raw retailer URL captured before
  //      `processVisualMatches` encrypts to a dupe.com redirect. This is
  //      the ONLY field that reflects the real retailer on cache-hit
  //      rebuilds, where `match.link` has been rewritten to
  //      https://dupe.com/redirect/<blob>.
  //   2. `shopKey` — normalized shop origin (also set by
  //      processVisualMatches). Accept either a full URL or a bare
  //      domain.
  //   3. `link` — last-resort fallback for matches that predate the
  //      redirect encryption pipeline.
  // Using `link` directly would collapse every cached match to
  // `<title>|dupe.com`, merging items from different retailers into one
  // RRF identity and inflating their cross-source rrfScore.
  const urlForDomain = match.originalLink || match.shopKey || match.link
  let domain = ''
  if (urlForDomain) {
    try {
      domain = new URL(urlForDomain).hostname
        .toLowerCase()
        .replace(/^www\./, '')
    } catch {
      domain = urlForDomain.toLowerCase()
    }
  }
  if (title && domain) return `${title}|${domain}`
  if (title) return `title:${title}`
  if (match.originalLink) return `link:${match.originalLink}`
  if (match.link) return `link:${match.link}`
  if (match.pid) return `pid:${match.pid}`
  // Deterministic fallback: hash the stringified match rather than a
  // random suffix. attachRrfScores calls stableMatchId twice (score
  // build + attach) so a Math.random fallback produced different IDs
  // on each pass and the item never picked up its rrfScore. Hashing a
  // stable JSON lets the two passes agree.
  try {
    return `anon:${JSON.stringify(match).slice(0, 200)}`
  } catch {
    return 'anon:unidentifiable'
  }
}

/**
 * Reciprocal Rank Fusion — merge N ranked lists into a single ranked
 * list without requiring their scores to be on the same scale.
 *
 *   rrf_score(item) = Σ  1 / (k + rank_in_source_i)
 *
 * Each source passes in its matches in its own native-sorted order (best
 * first). Items appearing high in multiple sources float to the top via
 * consensus; items appearing only once stay where their single source
 * put them.
 *
 * Why we use this: Google Lens, Turbopuffer, Shopify, and eBay each
 * return a ranked list on a different score scale (ordinal position,
 * cosine distance, relevance score, unknown). Calibrating them against a
 * common scalar (e.g. PUMICE cosine) requires a per-request embedding
 * fan-out that caps our throughput at PUMICE's concurrency. RRF gets us
 * cross-source ranking with zero external calls — O(N) arithmetic over
 * signals the backends already return. See
 * docs/features/backlog/visual-match-quality/phase-5-rrf-plan.md.
 *
 * The `k` constant (60 is the literature-standard default from Cormack
 * et al. 2009) dampens the top-rank dominance so the #1 result from one
 * source doesn't automatically win over two sources' agreement on a
 * slightly lower rank.
 */
export function rrfMerge<
  T extends {
    title?: string | null
    link?: string
    pid?: string
  },
>(
  sources: Array<{ name: string; matches: T[] }>,
  opts: { k?: number } = {}
): Array<T & { rrfScore: number; rrfSources: string[] }> {
  const k = opts.k ?? 60
  const byId = new Map<string, { match: T; score: number; sources: string[] }>()

  for (const src of sources) {
    src.matches.forEach((m, i) => {
      const id = stableMatchId(m)
      const rank = i + 1
      const contribution = 1 / (k + rank)
      const existing = byId.get(id)
      if (existing) {
        existing.score += contribution
        if (!existing.sources.includes(src.name)) {
          existing.sources.push(src.name)
        }
      } else {
        byId.set(id, { match: m, score: contribution, sources: [src.name] })
      }
    })
  }

  return Array.from(byId.values())
    .map(({ match, score, sources }) => ({
      ...match,
      rrfScore: score,
      rrfSources: sources,
    }))
    .sort((a, b) => b.rrfScore - a.rrfScore)
}

/**
 * Compute RRF scores across N per-source ranked lists and attach them
 * in-place to every match via `rrfScore` + `rrfSources` fields.
 *
 * Unlike `rrfMerge`, this does not dedupe — each source's array retains
 * its original entries. Useful in the controller where we want to score
 * matches but keep them flowing through the existing concat + filter +
 * dedup pipeline untouched. A single match object appearing in multiple
 * source lists gets the same combined score/sources on all instances,
 * since downstream dedup will collapse them anyway.
 */
export function attachRrfScores<
  T extends {
    title?: string | null
    link?: string
    pid?: string
    rrfScore?: number
    rrfSources?: string[]
  },
>(
  sources: Array<{ name: string; matches: T[] }>,
  opts: {
    k?: number
    /**
     * Per-match absolute-quality weight applied to each rank contribution
     * (default: 1 for everything = plain RRF). Useful when sources expose
     * an absolute similarity signal (Turbopuffer `$dist`, PUMICE
     * `visualScore`) in addition to their rank ordering — plain RRF
     * treats "TP rank 5 of 25" the same whether the absolute similarity
     * is 0.9 or 0.37, so a mediocre match can out-rank a strong one just
     * by being returned in a small source list. Clamped to [0.1, 1] so
     * no weighting ever zeros out an otherwise valid rank.
     *
     * `rankInSource` is the 1-based rank this match holds in its source's
     * list — the same rank the contribution below uses. A weight that is
     * derived from rank (Lens) must use it rather than a vendor field
     * stamped before filtering; see `lensQualityFromRank`.
     */
    qualityWeight?: (match: T, rankInSource: number) => number
  } = {}
): void {
  const k = opts.k ?? 60
  const weightFn = opts.qualityWeight
  const scoreMap = new Map<string, { score: number; sources: Set<string> }>()

  for (const src of sources) {
    // Within a single source, the same physical item can appear twice
    // (affiliate variants, different SKU URLs for the same product). If
    // we let every listing add its rank contribution, a single backend
    // can outvote real cross-source consensus — e.g. 3 Wayfair URL
    // variants at ranks 1/3/5 stack to 1/61 + 1/63 + 1/65 and beat a
    // genuine Lens+Turbopuffer agreement. Track seen IDs per source
    // and only count the BEST (first-encountered = lowest rank) per
    // source/item pair.
    const seenInSource = new Set<string>()
    src.matches.forEach((m, i) => {
      const id = stableMatchId(m)
      if (seenInSource.has(id)) return
      seenInSource.add(id)

      const rawWeight = weightFn ? weightFn(m, i + 1) : 1
      const weight =
        isFinite(rawWeight) && rawWeight > 0
          ? Math.min(1, Math.max(0.1, rawWeight))
          : 1
      const contribution = (1 / (k + i + 1)) * weight
      const existing = scoreMap.get(id)
      if (existing) {
        existing.score += contribution
        existing.sources.add(src.name)
      } else {
        scoreMap.set(id, {
          score: contribution,
          sources: new Set([src.name]),
        })
      }
    })
  }

  for (const src of sources) {
    for (const m of src.matches) {
      const entry = scoreMap.get(stableMatchId(m))
      if (entry) {
        m.rrfScore = entry.score
        m.rrfSources = Array.from(entry.sources)
      }
    }
  }
}

/**
 * How the rows passed to `attachRrfScoresFromMerged` were attributed to a
 * source: by `source_api`, by the `namespace` fallback (not a dependable
 * label), or not at all (`unlabelled` — dropped from scoring).
 */
export interface RrfLabelCounts {
  sourceApi: number
  namespaceOnly: number
  unlabelled: number
}

/**
 * Reconstruct per-source ranked lists from an already-merged match array
 * and attach RRF scores.
 *
 * Use this on the cache-hit path where we have a flat list of persisted
 * matches and need to score them for `SortCriteria.RrfRecommended`
 * without the original per-source buckets. We group by `source_api`
 * (falling back to `namespace`), then sort each group by its
 * source-native ranking signal:
 *
 *   - `turbopuffer`: `score` **ascending**. TP's `score` field is raw
 *     `$dist` from the vector search (see `transformTurbopufferProduct`
 *     at apps/api/src/lib/turbopuffer/transform.ts) — lower distance =
 *     more similar. Sorting descending puts the worst matches at rank 1
 *     which inverts consensus for the whole source.
 *   - everything else: `position` ascending (1 = best), then `score`
 *     descending as a tiebreaker for sources that carry both. Matches
 *     with neither sort behind those that have them.
 *
 * ⛔ Matches without an identifiable source are **dropped, not
 * scored**. They group under `'unknown'`, and that bucket is skipped
 * outright below — so they never reach `attachRrfScores` and keep
 * `rrfScore` undefined. That is load-bearing, not cosmetic: the
 * `RrfRecommended` comparator reads `rrfScore ?? -Infinity`
 * (`apps/api/src/lib/sortVisualMatches.ts:645`), so an unlabelled row
 * sorts **below every labelled row** in the grid.
 *
 * This is deliberate (see the `'skips unknown sources'` spec). The
 * returned `RrfLabelCounts` is how callers see it: the dupes controller
 * emits them as `dupes.rrf.rows` by `label`. Unlabelled rows cost us a
 * real regression — SerpAPI-served Lens rows reached the grid with no
 * `source_api`, were dropped here, and lost
 * slot 0 by walkover until #5342 stamped them. See
 * docs/features/active/text-search-retrieval/findings-image-path-relevance-gap-2026-09-25.md.
 *
 * ⚠️ `namespace` is NOT a dependable source label — `poshmark` puts a
 * seller id in it and `typesense` a category — so a new producer should
 * set `source_api` rather than rely on the `namespace` fallback.
 */
export function attachRrfScoresFromMerged<
  T extends {
    title?: string | null
    link?: string
    pid?: string
    source_api?: string
    namespace?: string
    position?: number
    score?: number
    visualScore?: number
    rrfScore?: number
    rrfSources?: string[]
  },
>(
  matches: T[],
  opts: {
    k?: number
    qualityWeight?: (match: T, rankInSource: number) => number
  } = {}
): RrfLabelCounts {
  const bySource = new Map<string, T[]>()
  const counts: RrfLabelCounts = {
    sourceApi: 0,
    namespaceOnly: 0,
    unlabelled: 0,
  }

  const sourceOf = (m: T): string => {
    const raw = (m.source_api || m.namespace || 'unknown').toLowerCase()
    // Normalize common aliases so grouping doesn't fragment the same
    // logical source across slightly different tags (Lens has two in
    // the wild: 'google_lens' and 'serpapi_lens' via older paths).
    if (raw === 'google_lens' || raw === 'serpapi_lens' || raw === 'lens')
      return 'lens'
    if (raw === 'amp_ad') return 'amp_ad'
    return raw
  }

  for (const m of matches) {
    if (m.source_api) counts.sourceApi++
    else if (m.namespace) counts.namespaceOnly++
    else counts.unlabelled++
    const src = sourceOf(m)
    const bucket = bySource.get(src)
    if (bucket) bucket.push(m)
    else bySource.set(src, [m])
  }

  const grouped: Array<{ name: string; matches: T[] }> = []
  for (const [name, items] of bySource.entries()) {
    // Skip AMP ads — those are sponsored and shouldn't influence
    // consensus scoring for organic results.
    if (name === 'amp_ad' || name === 'unknown') continue

    const sorted = [...items].sort((a, b) => {
      if (name === 'turbopuffer') {
        // TP `score` = raw $dist, lower is better. Missing scores sink
        // via +Infinity so untagged rows don't poison rank 1.
        const aScore = typeof a.score === 'number' ? a.score : Infinity
        const bScore = typeof b.score === 'number' ? b.score : Infinity
        return aScore - bScore
      }
      // Accept position >= 0 as valid: Shopify and eBay use 0-indexed
      // positions (the first match is position: 0), while Lens uses
      // 1-indexed. A strict `position >= 1` filter dropped every
      // top-ranked Shopify/eBay item to Infinity and corrupted the
      // source rank. Negative positions (e.g. -1 used to signal tier
      // boost in internal paths) are still treated as missing.
      const aPos =
        typeof a.position === 'number' && a.position >= 0
          ? a.position
          : Infinity
      const bPos =
        typeof b.position === 'number' && b.position >= 0
          ? b.position
          : Infinity
      if (aPos !== bPos) return aPos - bPos
      const aScore = typeof a.score === 'number' ? a.score : 0
      const bScore = typeof b.score === 'number' ? b.score : 0
      return bScore - aScore
    })
    grouped.push({ name, matches: sorted })
  }

  attachRrfScores(grouped, opts)
  return counts
}

function withDefaultHttpsProtocol(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/**
 * Extracts a "shop key" from a given URL.
 *
 * The "shop key" is designed to uniquely identify an e-commerce shop or domain
 * by returning its normalized origin. This is useful for deduplicating or indexing
 * shops based on their URLs, such as for grouping product results by originating store.
 *
 * - The function first checks if the input URL is provided. If not, it returns an empty string.
 * - If the URL doesn't start with a protocol (http:// or https://), it prepends 'https://' automatically.
 * - It attempts to construct a new URL object from the provided string. If the string is not a valid URL,
 *   it returns an empty string.
 * - The protocol is forcibly set to HTTPS (regardless of input protocol) for consistency,
 *   ensuring that all shop keys are standardized on HTTPS origins (e.g., `https://example.com`).
 * - The shop key is then returned as a string (the `.origin` property of the URL).
 * - If any error is thrown (typically for invalid URLs), the function returns an empty string.
 *
 * @param url The URL string from which to extract the shop key. May be null, undefined, or an invalid URL.
 * @returns A normalized shop origin string (e.g., 'https://examplestore.com'), or an empty string if invalid.
 *
 * @example
 *   getShopKey('http://examplestore.com/some/page')  // returns 'https://examplestore.com'
 *   getShopKey('www.examplestore.com/some/path')     // returns 'https://www.examplestore.com'
 *   getShopKey(null)                                 // returns ''
 */
export const getShopKey = (url: string | null | undefined): string => {
  if (!url) return ''
  try {
    const shopUrl = new URL(withDefaultHttpsProtocol(url))
    shopUrl.protocol = 'https:'
    return shopUrl.origin
  } catch (_e) {
    return ''
  }
}

function isRegistrableIcannHost(host: string): boolean {
  const parsed = parseDomain(host)
  return !!(parsed.domain && parsed.publicSuffix && parsed.isIcann)
}

function stripAnalyticsShopHost(hostname: string): string {
  let host = hostname.toLowerCase()

  while (SHOP_DOMAIN_ALIAS_PREFIX_RE.test(host)) {
    const stripped = host.replace(SHOP_DOMAIN_ALIAS_PREFIX_RE, '')
    if (!isRegistrableIcannHost(stripped)) {
      break
    }
    host = stripped
  }

  return host
}

/**
 * Analytics-only shop key normalization for Tinybird and dashboard grouping.
 *
 * Unlike getShopKey(), this intentionally strips one or more leading common
 * retail utility subdomains so impressions for www./m./secure. variants
 * aggregate together. Do not use this value for DB foreign keys; persisted
 * shops.key values may intentionally include those subdomains.
 */
export const getAnalyticsShopKey = (url: string | null | undefined): string => {
  if (!url) return ''
  try {
    const shopUrl = new URL(withDefaultHttpsProtocol(url))
    const host = stripAnalyticsShopHost(shopUrl.hostname)
    const port = shopUrl.port ? `:${shopUrl.port}` : ''
    return `https://${host}${port}`
  } catch (_e) {
    return ''
  }
}

/**
 * Checks if a shopKey is a Dupe redirect domain (dupe.com or dupe.link).
 *
 * This happens when cached/reloaded results have already-encrypted links,
 * so getShopKey() extracts "https://dupe.com" instead of the real retailer.
 */
export const isDupeShopKey = (shopKey: string | undefined): boolean => {
  if (!shopKey) return false
  // Match exact domain, paths (/redirect/...), query strings (?ref=...), and fragments (#...)
  return (
    shopKey === 'https://dupe.com' ||
    shopKey.startsWith('https://dupe.com/') ||
    shopKey.startsWith('https://dupe.com?') ||
    shopKey.startsWith('https://dupe.com#') ||
    shopKey === 'https://dupe.link' ||
    shopKey.startsWith('https://dupe.link/') ||
    shopKey.startsWith('https://dupe.link?') ||
    shopKey.startsWith('https://dupe.link#')
  )
}

export const isValidUrl = (url?: string): URL | false => {
  if (!url) return false

  try {
    let urlToParse = url

    // if url does not start with http or https, validate it's a domain before prepending
    if (!url.startsWith('http:/') && !url.startsWith('https:/')) {
      // Use tldts to check if this looks like a valid domain
      const parsed = parseDomain(url)

      // Only prepend https:// if we have a valid domain and TLD
      if (!parsed.domain || !parsed.publicSuffix) {
        return false
      }

      urlToParse = `https://${url}`
    }

    // if url has invalid origin e.g https://.www. clean it up
    if (urlToParse.startsWith('https://.www.')) {
      urlToParse = urlToParse.replace('https://.www.', 'https://www.')
    }

    const urlObj = new URL(urlToParse)
    return urlObj.hostname.indexOf('.') > -1 ? urlObj : false
  } catch (_e) {
    return false
  }
}

/**
 * Validates if a string is a valid image URL.
 *
 * Rejects concatenated URL lists (`a.jpg, b.jpg`) but accepts single URLs whose
 * path contains commas — common in Cloudinary / Akamai image transforms used
 * by Nike, Adidas, etc. (`.../u_abc,c_scale,w_1.0/img.png`) — and single URLs
 * that embed another URL in a query parameter, such as image proxies
 * (`https://imgproxy.example.com/?url=https://cdn.shop.com/p.jpg`).
 *
 * Heuristic: a concatenated list is two URLs glued together with a separator,
 * so reject only when a `,` (optionally followed by whitespace) precedes a
 * second `http(s)://` scheme. Embedded-URL patterns are preceded by `=`, `?`,
 * or `/`, not a separator, and pass through.
 *
 * Shared between `apps/api/src/lib/scraper/runScrapingStrategy.ts` and
 * `apps/scrape` so both scrape paths agree on which image URLs survive.
 */
export const isValidImageUrl = (
  imageUrl: string | undefined | null
): boolean => {
  if (!imageUrl || typeof imageUrl !== 'string') return false

  // Unencoded spaces never appear in valid URLs (should be %20). This also
  // catches space-delimited concatenated lists.
  if (imageUrl.includes(' ')) {
    return false
  }

  // Comma-delimited concatenated list: `,` immediately before another scheme.
  if (/,\s*https?:\/\//i.test(imageUrl)) {
    return false
  }

  return !!isValidUrl(imageUrl)
}

/**
 * RH PDP preset. Mirrors the `?$PDP-IS-1600$` branch in
 * `packages/data/sql/functions/fix_results_image_urls.sql` (rh.com case) —
 * keep both in sync. Not `?$PD$`, which serves an "image available soon" placeholder.
 */
const RH_SCENE7_PDP_PRESET = '?$PDP-IS-1600$'

/**
 * Normalize product image URLs from scrape metadata (og:image, JSON-LD, APIs).
 * Generic for all shops:
 * - Decodes HTML entities (`&amp;` → `&`) common in meta tags
 * - Decodes `%26` → `&` (URL-semantically always a literal `&`)
 * - Strips trailing lone `?` / `?&`
 *
 * RH Scene7-only (host: `*.restorationhardware.com`, path: `/is/image/rhis/`):
 * - Full percent-decode of the remaining string
 * - Keep dynamic templates and close truncated `src=ir(…)`
 * - For bare `/rhis/prod…` assets apply {@link RH_SCENE7_PDP_PRESET}
 *
 * Overstock CDN-only (host: `*.ostkcdn.com`): restore the `/images/products/`
 * path prefix that the Unwrangle scraper drops — see
 * {@link restoreOstkcdnProductsPrefix}.
 *
 * Doubled scheme (any host): a source page sometimes already serves an
 * absolute `http(s)://` image URL, and something upstream of us (their own
 * templating, not ours) prepends another scheme on top — e.g.
 * `https://http://host/img.jpg`. `new URL()` parses that "successfully" but
 * with hostname `http`, so any real fetch of it fails against a host that
 * doesn't exist. Strip the redundant leading scheme so only the real one
 * remains — see {@link stripDoubledProtocol}.
 */
export function normalizeProductImageUrl(
  imageUrl: string | undefined | null
): string | undefined | null {
  if (imageUrl == null || typeof imageUrl !== 'string') return imageUrl

  let cleaned = imageUrl.trim()
  if (!cleaned) return cleaned

  cleaned = stripDoubledProtocol(cleaned)

  cleaned = cleaned
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')

  if (cleaned.includes('%26')) {
    cleaned = cleaned.replace(/\?%26/gi, '?&').replace(/%26/gi, '&')
  }

  cleaned = normalizeRhScene7ImageUrl(cleaned)
  cleaned = restoreOstkcdnProductsPrefix(cleaned)

  if (cleaned.endsWith('?') || cleaned.endsWith('?&')) {
    cleaned = cleaned.replace(/\?&?$/, '')
  }

  return cleaned
}

/**
 * Collapse a doubled scheme down to a single `https://` — e.g.
 * `https://http://host/img.jpg` → `https://host/img.jpg`, and
 * `http://https://host/img.jpg` / `http://http://host/img.jpg` also →
 * `https://host/img.jpg`. `new URL()` on the original doubled string parses
 * "successfully" with hostname `http`/`https`, which is never a real host —
 * any real fetch of it fails.
 *
 * Always emitting `https://` (rather than keeping whichever scheme was
 * outermost) is required, not just a preference: the `results` table's
 * `fix_image` BEFORE INSERT/UPDATE trigger (`packages/data/sql/functions/
 * normalize_image_url.sql`) unconditionally prepends `https://` to any value
 * that doesn't already start with it. An outer-`http` result from this
 * function would come back out of that trigger as `https://http://host/...`
 * — the identical corruption this function exists to remove — on the very
 * write meant to persist the fix. Always producing `https://` here makes the
 * trigger a no-op on our output for all four doubled-scheme combinations.
 *
 * Deliberately narrower than {@link normalizeProductImageUrl} (which calls
 * this as its first step): this is the function to reach for at a call site
 * that only needs the double-protocol bug fixed — an ingestion path that
 * never ran any normalization before shouldn't also start getting HTML-entity
 * decoding, RH Scene7 template rewriting, or Overstock prefix restoration for
 * free, since those are unrelated behavior changes for that path.
 */
export function stripDoubledProtocol<T extends string | undefined | null>(
  url: T
): T {
  if (url == null) return url
  // Runtime data doesn't always match the declared type — some retailers emit
  // JSON-LD-shaped `{ "@value": ..., "@language": "en" }` objects in image
  // arrays instead of plain strings. `.replace` on those throws and crashes
  // the whole render, so bail out instead of trusting the generic's promise.
  if (typeof url !== 'string') return url
  return url.replace(/^https?:\/\/https?:\/\//i, 'https://') as T
}

function isOstkcdnHost(hostname: string): boolean {
  return hostname === 'ostkcdn.com' || hostname.endsWith('.ostkcdn.com')
}

/**
 * Restore the `/images/products/` prefix on Overstock CDN image URLs.
 *
 * The Unwrangle scraper (`strategy: unwrangle:api`) returns Bed Bath & Beyond
 * PDP images with that path segment missing — 1,139 of 1,143 ostkcdn rows in a
 * 14-day sample, vs ~0 from every other strategy. Overstock retired the
 * prefix-less path around 2026-07-24, so those URLs began hard-404ing and
 * became the single largest source of PUMICE embed failures (31% of all
 * proxy-ladder attempts, at a 0% recovery rate — no proxy tier can rescue a
 * 404, they all fetch the same missing object).
 *
 * ```
 * in   https://ak1.ostkcdn.com/is/images/direct/<sha>/Name.jpg          → 404
 * out  https://ak1.ostkcdn.com/images/products/is/images/direct/<sha>/Name.jpg → 200
 * ```
 *
 * Deliberately narrow: only the two shapes Overstock actually serves are
 * rewritten — Scene7 (`/is/image[s]/…`) and the legacy numeric asset
 * (`/<digits>/Name.jpg`). Anything already carrying the prefix, and any other
 * ostkcdn path, is returned untouched, so this is idempotent and can't corrupt
 * a URL that was already correct.
 */
function restoreOstkcdnProductsPrefix(input: string): string {
  // Cheap substring bail-out before paying for `new URL()`. This helper sits on
  // the shared scrape/parse choke point, so it runs for every image URL on
  // every host — the parse is wasted work for all of them but Overstock. Same
  // guard pattern as `isWayfairCdnUrl` (review on #3698). Purely a negative
  // fast path: the exact-hostname check below still decides the positive case,
  // so a URL that merely mentions ostkcdn.com in a query param is still
  // rejected.
  if (!input.includes('ostkcdn.com')) return input

  try {
    const parsed = new URL(input)
    if (!isOstkcdnHost(parsed.hostname)) return input
    // Case-insensitive to match the shape regexes below. Overstock serves
    // lowercase paths in practice; keeping the three checks consistent avoids a
    // mixed-case path slipping past the guard and getting double-prefixed.
    if (/^\/images\/products\//i.test(parsed.pathname)) return input

    const isScene7 = /^\/is\/images?\//i.test(parsed.pathname)
    const isLegacyNumericAsset = /^\/\d+\/[^/]+\.[a-z0-9]+$/i.test(
      parsed.pathname
    )
    if (!isScene7 && !isLegacyNumericAsset) return input

    parsed.pathname = `/images/products${parsed.pathname}`
    return parsed.toString()
  } catch {
    return input
  }
}

function isRhScene7Host(hostname: string): boolean {
  return (
    hostname === 'restorationhardware.com' ||
    hostname.endsWith('.restorationhardware.com')
  )
}

function normalizeRhScene7ImageUrl(input: string): string {
  let working = input
  try {
    const parsed = new URL(working)
    if (!isRhScene7Host(parsed.hostname)) return working
    if (!parsed.pathname.includes('/is/image/rhis/')) return working

    // Scoped percent-decode — RH Scene7 URLs frequently arrive double/triple
    // encoded (`%2F`, `%3D`, `%28`, `%29`). Safe here because the shape is
    // RH-specific; we don't re-encode for fetch.
    if (/%[0-9A-Fa-f]{2}/.test(working)) {
      try {
        working = decodeURIComponent(working)
      } catch {
        /* keep partially decoded */
      }
    }

    const reparsed = (() => {
      try {
        return new URL(working)
      } catch {
        return parsed
      }
    })()
    const pathSeg = reparsed.pathname.split('/').pop() ?? ''

    // Dynamic template (e.g. /rhis/4KH?…src=ir(rhir/prod…)) — repair, do not flatten to prod path
    if (
      /src=ir\(/i.test(working) ||
      (!/^prod\d/i.test(pathSeg) && reparsed.search)
    ) {
      let fixed = working.replace(/\?&+/, '?')
      if (/src=ir\([^)]*$/i.test(fixed)) {
        // If the truncation looks mid-modifier (trailing `,` or `,$key=` with
        // no value), the closed template would render wrong. Floor to the bare
        // prod asset + PDP preset — the canonical fallback the SQL trigger lands on.
        const malformedTrailing = /,\s*(\$[^=,)]*=?)?$/
        const prodMatch = fixed.match(/src=ir\(rhir\/(prod[\w-]+)/i)
        if (malformedTrailing.test(fixed) && prodMatch) {
          return `${reparsed.origin}/is/image/rhis/${prodMatch[1]}${RH_SCENE7_PDP_PRESET}`
        }
        fixed += ')'
      }
      return fixed
    }

    // Bare `/rhis/prod…` asset — apply PDP preset (mirror SQL trigger).
    if (/^prod\d/i.test(pathSeg)) {
      if (/\?\$[A-Za-z][A-Za-z0-9_-]*\$/.test(working)) {
        return working.replace(
          /\?\$[A-Za-z][A-Za-z0-9_-]*\$.*$/,
          RH_SCENE7_PDP_PRESET
        )
      }
      if (
        !reparsed.search ||
        reparsed.search === '?' ||
        /\?\$PD\$/i.test(working)
      ) {
        return `${reparsed.origin}${reparsed.pathname}${RH_SCENE7_PDP_PRESET}`
      }
    }

    return working
  } catch {
    return working
  }
}

/** PDP-sized edge when we replace tiny Adobe DM thumbs (e.g. og:image). */
const ADOBE_DM_TARGET_EDGE = 1200

/** Below this max(wid,hei), treat as a thumbnail and upsize (DSG, UO, Vans, etc.). */
const ADOBE_DM_MIN_MEANINGFUL_EDGE = 250

/**
 * Adobe Dynamic Media / Scene7-style URLs use `wid` / `hei` query params.
 * Retailers often set og:image to a 16×16 or 50×50 thumb; bump to a usable size.
 *
 * Only touches URLs whose path includes `/is/image/` and that already specify
 * at least one of `wid` or `hei`.
 */
export function upsizeTinyAdobeDynamicMediaImageUrl(
  imageUrl: string | undefined | null
): string | undefined | null {
  if (imageUrl == null || typeof imageUrl !== 'string') return imageUrl

  try {
    const u = new URL(imageUrl)
    if (!u.pathname.includes('/is/image/')) return imageUrl

    const widRaw = u.searchParams.get('wid')
    const heiRaw = u.searchParams.get('hei')
    if (widRaw == null && heiRaw == null) return imageUrl

    const w = widRaw != null ? Number.parseInt(widRaw, 10) : 0
    const h = heiRaw != null ? Number.parseInt(heiRaw, 10) : 0
    const maxEdge = Math.max(
      Number.isFinite(w) && w > 0 ? w : 0,
      Number.isFinite(h) && h > 0 ? h : 0
    )

    if (maxEdge === 0 || maxEdge >= ADOBE_DM_MIN_MEANINGFUL_EDGE) {
      return imageUrl
    }

    if (widRaw != null && Number.isFinite(w) && w > 0) {
      u.searchParams.set('wid', String(ADOBE_DM_TARGET_EDGE))
    }
    if (heiRaw != null && Number.isFinite(h) && h > 0) {
      u.searchParams.set('hei', String(ADOBE_DM_TARGET_EDGE))
    }

    return u.toString()
  } catch {
    return imageUrl
  }
}

const SCHEMA_IMAGE_COLLECT_MAX_DEPTH = 8

const LIKELY_THUMB_PATH =
  /\/(thumb|thumbnails?|icons?)(\/|$)|favicon|\/sprite|placeholder|1x1|pixel\.gif/i

/**
 * Flatten schema.org `image` / `images` values (strings, ImageObject, arrays, @list) into URL strings.
 * Used to compare multiple declared assets and pick a full-size hero instead of the first entry (often a thumb).
 */
export function collectSchemaImageUrls(value: unknown, depth = 0): string[] {
  if (depth > SCHEMA_IMAGE_COLLECT_MAX_DEPTH || value == null) return []

  if (typeof value === 'string') {
    const t = value.trim()
    if (!t || t === 'null' || t === 'undefined') return []
    return [t]
  }

  if (Array.isArray(value)) {
    return value.flatMap((v) => collectSchemaImageUrls(v, depth + 1))
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (Array.isArray(obj['@list'])) {
      return collectSchemaImageUrls(obj['@list'], depth + 1)
    }

    const urls: string[] = []
    for (const key of ['url', 'contentURL', 'contentUrl', 'thumbnail']) {
      const v = obj[key]
      if (typeof v === 'string' && v.trim()) urls.push(v.trim())
    }
    if (obj.image != null) {
      urls.push(...collectSchemaImageUrls(obj.image, depth + 1))
    }
    return urls
  }

  return []
}

function scoreProductImageCandidate(url: string): number {
  try {
    const u = new URL(url)
    const href = u.href
    const path = u.pathname.toLowerCase()
    let score = 0

    if (LIKELY_THUMB_PATH.test(path) || LIKELY_THUMB_PATH.test(href)) {
      score -= 100_000
    }

    if (path.includes('/is/image/')) {
      const w = Number.parseInt(u.searchParams.get('wid') || '0', 10)
      const h = Number.parseInt(u.searchParams.get('hei') || '0', 10)
      const maxDim = Math.max(
        Number.isFinite(w) && w > 0 ? w : 0,
        Number.isFinite(h) && h > 0 ? h : 0
      )
      if (maxDim > 0) {
        score += maxDim * 1_000
      } else {
        score += 80_000
      }
      return score
    }

    for (const param of ['width', 'w', 'wid']) {
      const raw = u.searchParams.get(param)
      if (raw) {
        const n = Number.parseInt(raw, 10)
        if (Number.isFinite(n) && n > 0) {
          score += Math.min(n, 4_000)
          break
        }
      }
    }

    score += Math.min(path.length, 240)
    return score
  } catch {
    return Number.NEGATIVE_INFINITY
  }
}

/**
 * Choose the best product image from structured-data candidates (higher-res Adobe DM, width params, etc.).
 * Invalid / concatenated URLs are dropped via {@link isValidImageUrl}.
 */
export function pickBestProductImageUrl(
  candidates: string[]
): string | undefined {
  const unique = [
    ...new Set(
      candidates
        .map((c) => normalizeProductImageUrl(c.trim()) ?? '')
        .filter(Boolean)
    ),
  ]
  const valid = unique.filter((u) => isValidImageUrl(u))
  if (valid.length === 0) return undefined

  let best = valid[0]!
  let bestScore = scoreProductImageCandidate(best)
  for (let i = 1; i < valid.length; i++) {
    const u = valid[i]!
    const s = scoreProductImageCandidate(u)
    if (s > bestScore || (s === bestScore && u.length > best.length)) {
      best = u
      bestScore = s
    }
  }
  return best
}

const isSrcsetSpace = (c: string | undefined) =>
  c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f'

/**
 * Split a `srcset` into its candidates, following the HTML spec's parsing rules
 * rather than `split(',')`.
 *
 * ⚠️ A comma is NOT a candidate separator on its own. URLs may legally contain
 * commas, and image CDNs use them all the time (Cloudinary transforms read
 * `c_crop,ar_1920:2623,g_south/q_auto,f_auto/...`). A naive comma split breaks the
 * URL into pieces and hands back a fragment like `w_1800/f26_a03_134315_on_a`,
 * which resolves relative to the merchant's page, 404s, and silently hides the
 * extension's on-page tab: every Aritzia product page, measured in Tinybird.
 *
 * Per spec, a URL is a run of non-whitespace. Trailing commas on that run end the
 * candidate; otherwise the descriptors run to the next comma outside parentheses.
 */
export function parseSrcset(
  srcset: string
): Array<{ url: string; descriptor: string }> {
  const candidates: Array<{ url: string; descriptor: string }> = []
  const len = srcset.length
  let pos = 0
  while (pos < len) {
    while (pos < len && (isSrcsetSpace(srcset[pos]) || srcset[pos] === ','))
      pos++
    if (pos >= len) break

    const urlStart = pos
    while (pos < len && !isSrcsetSpace(srcset[pos])) pos++
    let url = srcset.slice(urlStart, pos)

    let descriptor = ''
    if (url.endsWith(',')) {
      url = url.replace(/,+$/, '')
    } else {
      const descStart = pos
      let inParens = false
      while (pos < len) {
        const c = srcset[pos]
        if (c === '(') inParens = true
        else if (c === ')') inParens = false
        else if (c === ',' && !inParens) break
        pos++
      }
      descriptor = srcset.slice(descStart, pos).trim()
      pos++ // past the separating comma
    }
    // Off-spec but seen in the wild: `a.jpg,https://…/b.jpg` with no spaces or
    // descriptors. Browsers read that as one URL; the old comma split read it
    // as several and pages may rely on it, so split where a comma is directly
    // followed by the start of another URL. A comma INSIDE a URL (Cloudinary's
    // `c_crop,ar_…`) is never followed by `/` or a scheme, so it stays put.
    const pieces = url.split(/,(?=(?:https?:)?\/)/i)
    pieces.forEach((piece, i) => {
      if (piece)
        candidates.push({
          url: piece,
          descriptor: i === pieces.length - 1 ? descriptor : '',
        })
    })
  }
  return candidates
}

/**
 * The largest candidate in a `srcset`: the widest by `w` descriptor; else, when
 * no candidate carries a width, the densest by `x` descriptor (`2x` beats `1x`);
 * else the first candidate. `null` for an empty srcset.
 */
export function largestSrcFromSrcset(srcset: string): string | null {
  const candidates = parseSrcset(srcset)
  const pick = (re: RegExp): string | null => {
    let best: string | null = null
    let bestSize = 0
    for (const { url, descriptor } of candidates) {
      const match = re.exec(descriptor)
      const size = match ? parseFloat(match[1]!) : 0
      if (size > bestSize) {
        bestSize = size
        best = url
      }
    }
    return best
  }
  return (
    pick(/^(\d+)w$/i) ??
    pick(/^(\d+(?:\.\d+)?)x$/i) ??
    candidates[0]?.url ??
    null
  )
}

/**
 * Returns true if the URL is already going through our proxy (current or legacy domains).
 * Use this instead of hardcoding domain checks to keep backward compat in one place.
 */
export const isProxiedUrl = (url: string): boolean => {
  return (
    url.includes('proxy.dupe.com') ||
    url.includes('img-proxy.crrt.dev') ||
    url.includes('img-proxy.dupe.dev') ||
    url.includes('img-proxy.dupe.com') ||
    url.includes('url-proxy.dupe.dev') ||
    url.includes('url-proxy.crrt.dev')
  )
}

/**
 * Convert an image URL to use the proxy service
 * @param url The original image URL
 * @param options Configuration options including optional image transforms
 * @returns The proxied image URL
 */
export const imgProxy = (
  url: string | null | undefined,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'auto' | 'webp' | 'avif' | 'jpeg' | 'png'
    blur?: number
  } = {}
): string => {
  if (!url) return ''

  // If already using proxy, return as is
  if (isProxiedUrl(url)) {
    return url
  }

  // Validate URL before encoding
  const validUrl = isValidUrl(url)
  if (!validUrl) {
    return ''
  }

  // DUP-2085: encode the *validated* URL, not the raw input. Product feeds and
  // Shopify themes routinely emit protocol-relative (`//cdn.shopify.com/x.jpg`)
  // or bare-host (`cdn.shopify.com/x.jpg`) image URLs. `isValidUrl` already
  // normalizes those to an absolute https URL, but we used to encode the raw
  // string, so the proxy received a scheme-less target and answered 400.
  // Only substitute for the scheme-less class: absolute http(s) inputs keep
  // their exact original bytes, so no currently-working URL changes shape.
  const hasScheme = /^https?:\/\//i.test(url)
  const urlToEncode = hasScheme ? url : validUrl.href

  // DUP-2332: a `forceCors` option used to return a corsproxy.io URL instead.
  // Removed with its last caller — corsproxy.io stopped serving our account
  // (403 on every server-side request), and PROXY_BASE_URL already answers with
  // the CORS headers a browser needs to read the bytes
  // (access-control-allow-origin reflects the requesting origin), so no caller
  // needed a third party for that either.
  let proxyUrl = `${PROXY_BASE_URL}/${safeEncodeUrl(urlToEncode)}`

  // Append image transform params if any
  const params = new URLSearchParams()
  if (options.width) params.set('w', String(options.width))
  if (options.height) params.set('h', String(options.height))
  if (options.quality) params.set('q', String(options.quality))
  if (options.format) params.set('f', options.format)
  if (options.blur) params.set('blur', String(options.blur))
  const qs = params.toString()
  if (qs) proxyUrl += `?${qs}`

  return proxyUrl
}

/**
 * Proxy an image through ImageKit
 * @param url The original image URL
 * @returns The proxied image URL
 */
export const proxyImageWithImageKit = (url: string) => {
  if (!url) return ''
  const validUrl = isValidUrl(url)
  if (!validUrl) {
    return ''
  }
  // Deliberately NOT routed through `buildImageKitUrl` — see the
  // "persisted URLs" warning on `IMAGEKIT_BLOCKED_HOSTS`. This helper's output
  // is stored and later re-parsed (`isPermanentlyUploaded` and
  // `unwrapProxyUrl` in researchProductImagesRehost, `unwrapImageKitProxy` in
  // internal-mcp, `normalizeImageUrl` in the researcher control plane), and
  // every one of those readers keys on the literal `://` this concatenated
  // form carries.
  //
  // Its callers (Notion gift guides, research images, lens results) CAN carry
  // a blocked retailer URL, so the shape is repaired at RENDER time instead —
  // `proxyExternalImage` for tiles, `repairImageKitUrl` for components that
  // render this stored value directly (gift-guide, obsessions, SEO report).
  // A new component rendering one of these URLs must call `repairImageKitUrl`,
  // or a blocked host will show a blank image.
  const imageKitProxyUrl = `${IMAGEKIT_URL_ENDPOINT}${url}`
  return imageKitProxyUrl
}

/**
 * A currency symbol → ISO-4217 code, or `undefined` when we cannot map it.
 *
 * ⚠️ **`undefined`, never the input.** The old version handled three symbols and had
 * `default: return symbol`, so any unmapped string landed in the column verbatim —
 * ~6,300 junk `pdp_view` rows a week across ~30 shops, values like `S` `C` `A` `R` `M`
 * (DUP-1928). An unset currency is honest and filterable; a wrong one is neither, and
 * it is worse than absent because currency GATES verdict eligibility: a cart recorded
 * as `"S"` can never receive a verdict, silently.
 *
 * ⚠️ **Longest match first.** `R$` must beat `$`, or every Brazilian price becomes USD.
 * The map below is ordered and the loop returns on first hit — do not sort it
 * alphabetically.
 *
 * ⚠️ **`kr` is deliberately UNMAPPED.** It is DKK, SEK, NOK or ISK depending on country
 * and nothing here can tell which. `pricing.ts` currently maps it to DKK in one place
 * and SEK in another (DUP-1851) — that contradiction is the evidence that picking a
 * winner encodes a coin-flip. `docs/adr/0006` reaches the same conclusion about
 * bare-symbol inference. Declining is the only honest answer.
 */
const CURRENCY_SYMBOLS: [string, string][] = [
  // Multi-character first — a prefix of a shorter symbol must not win.
  ['US$', 'USD'],
  ['NZ$', 'NZD'],
  ['HK$', 'HKD'],
  ['R$', 'BRL'],
  ['C$', 'CAD'],
  ['A$', 'AUD'],
  ['S$', 'SGD'],
  ['CHF', 'CHF'],
  ['DH', 'MAD'],
  ['RM', 'MYR'],
  ['Rp', 'IDR'],
  ['zł', 'PLN'],
  // Single character.
  ['$', 'USD'],
  ['£', 'GBP'],
  ['€', 'EUR'],
  ['¥', 'JPY'],
  ['₹', 'INR'],
  ['₩', 'KRW'],
  ['₪', 'ILS'],
  ['₺', 'TRY'],
  ['฿', 'THB'],
  ['₫', 'VND'],
]

export const currencySymbolToCode = (symbol: string): string | undefined => {
  const s = symbol?.trim()
  if (!s) return undefined
  // Already a code — pass it through rather than failing a valid input.
  if (/^[A-Z]{3}$/.test(s)) return s
  for (const [sym, code] of CURRENCY_SYMBOLS) {
    if (s === sym) return code
  }
  return undefined
}

/**
 * Safely encode a URL, preventing double encoding
 * @param url The URL to encode
 * @returns The safely encoded URL
 */
export const safeEncodeUrl = (url: string) => {
  try {
    // Try to decode first in case it's already encoded
    const decoded = decodeURIComponent(url)
    const cleaned = decoded.replace("'", '')
    // Only replace + with %2B for Bing URLs
    const final = url.includes('th.bing.com')
      ? cleaned.replace(/\+/g, '%2B')
      : cleaned
    return encodeURIComponent(final)
  } catch {
    // If decoding fails, it wasn't encoded, so encode it
    const cleaned = url.replace("'", '')
    // Only replace + with %2B for Bing URLs
    const final = url.includes('th.bing.com')
      ? cleaned.replace(/\+/g, '%2B')
      : cleaned
    return encodeURIComponent(final)
  }
}

export const NORMALIZE_OPTS: NormalizeOptions = {
  stripProtocol: false,
  stripWWW: false,
  removeTrailingSlash: true,
  sortQueryParameters: true,
  removeSingleSlash: true,
  // drop common tracking params
  removeQueryParameters: [/^utm_/i, 'fbclid', 'gclid', 'msclkid', 'ref'],
  stripHash: false,
  stripTextFragment: true,
  stripAuthentication: true,
}

/**
 * Merchants that can be embedded inside a next2.io (Ad Net) wrapper, keyed
 * by the bare `www.<domain>` marker to look for. Matched against the RAW
 * (often percent-encoded) url — the embedded `link` param is typically
 * `https%3A%2F%2Fwww.target.com%2F...`, where `://` is encoded but the
 * hostname isn't, so the marker deliberately omits the scheme (mirrors
 * merchantKey.ts's unmaskTrackingWrapperOrigin — matching on the full
 * `https://` origin here would never hit an encoded link param).
 */
const NEXT2_IO_SHOP_KEYS: Array<{ marker: string; origin: string }> = [
  { marker: 'www.target.com', origin: 'https://www.target.com' },
  { marker: 'www.amazon.com', origin: 'https://www.amazon.com' },
  {
    marker: 'www.bedbathandbeyond.com',
    origin: 'https://www.bedbathandbeyond.com',
  },
]

export const getNext2IoShopKey = (url: string): string => {
  // If not a next2.io URL, use the standard getShopKey function
  if (!url.includes('next2.io')) {
    return getShopKey(url)
  }

  // Check for mapped shop keys in the URL
  for (const { marker, origin } of NEXT2_IO_SHOP_KEYS) {
    if (url.includes(marker)) {
      return origin
    }
  }

  // Default fallback to url domain
  return getShopKey(url)
}

/**
 * True when a query string would be corrupted by a URLSearchParams rebuild.
 *
 * Two shapes, both common in CDN image URLs (Adobe Scene7 in particular):
 *
 * 1. **Nested query** — an `&` or `?` inside a parenthesised param value, e.g.
 *    `?src=ir(rhir/prod123?obj=Countertop&illum=0)&wid=461`. URLSearchParams
 *    splits on the inner `&`, so one param becomes several fragments
 *    (`src=ir(rhir/prod123?obj=Countertop`, `illum=0)`), and the alphabetical
 *    sort in NORMALIZE_OPTS then scatters them. Reassembly is impossible once
 *    sorted, so the URL is silently broken.
 *
 * 2. **Valueless param** — a segment with no `=`, e.g. Scene7 presets
 *    `?$img320$` or path-style params `?imageView2/2/w/500/format/avif`.
 *    `URLSearchParams.toString()` appends `=`, producing `?$img320$=`.
 *
 * Both are left untouched rather than cleaned: these are asset URLs that don't
 * carry affiliate/tracking params in practice, so there is nothing to strip,
 * and a corrupted asset URL fails to render. Product-page URLs — the ones that
 * do carry tracking params — always use `key=value` segments and so are
 * unaffected by this guard.
 */
function hasFragileQuery(search: string): boolean {
  if (!search || search === '?') return false
  const query = search.startsWith('?') ? search.slice(1) : search

  // Shape 1: a separator character inside parentheses.
  if (/\([^)]*[&?][^)]*\)/.test(query)) return true

  // Shape 2: any non-empty segment without a `=`. Empty segments are skipped
  // so a leading/duplicated `&` (`?&color=x`, `?a=1&&b=2`) isn't misread as
  // valueless — URLSearchParams drops those harmlessly.
  return query
    .split('&')
    .some((segment) => segment.length > 0 && !segment.includes('='))
}

class URLCleaner {
  // tracking params to remove
  private trackingParams: Set<string>
  // prefixes that identify tracking params (matched with startsWith)
  private trackingPrefixes: string[]
  // hostname specific params to keep
  private importantParams: Record<string, Set<string>>

  constructor() {
    // Known tracking params to remove (all lowercase for case-insensitive comparison)
    this.trackingParams = new Set([
      // Amazon ('psc' is also in importantParams for amazon.com — here it's stripped
      // for non-Amazon sites where it's a generic tracking param, but preserved on Amazon
      // where it's the "product size/color" selector)
      'tag',
      'linkcode',
      'linkid',
      'ref_',
      'ref',
      'pd_rd_r',
      'pd_rd_w',
      'pd_rd_wg',
      'psc',
      'refrid',
      'colid',
      'coliid',
      // Google Analytics / Ads (utm_* handled by prefix, but keep explicit for clarity)
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'gclid',
      'gclsrc',
      'dclid',
      'gad_source',
      'srsltid',
      'campaignid',
      'adgroupid',
      'targetid',
      'pla_sku',
      'pcat',
      'scid',
      'sc_intid',
      'mrkgcl',
      // Facebook
      'fbclid',
      'fb_action_ids',
      'fb_action_types',
      'fb_source',
      // Affiliate networks — Dupe redirect params
      'subid1', // Impact
      'u1', // Rakuten
      'awinmid', // Awin
      'awinaffid', // Awin
      'clickref', // Awin / Partnerize
      'fobs', // FlexOffers
      'foid', // FlexOffers
      'trid', // FlexOffers
      'dupe_feed', // FlexOffers feed flag
      'atid', // LinkConnector
      'ctc', // AvantLink
      'cuid', // Sovrn
      'af_rid', // Connexity
      'publisherid', // Connexity
      'subid', // BBB / Admedia
      'customid', // eBay EPN
      'siteid', // eBay EPN
      'mkcid', // eBay EPN
      'mkrid', // eBay EPN
      'campid', // eBay EPN
      'toolid', // eBay EPN
      'mkevt', // eBay EPN
      '_trkparms', // eBay
      'clkid', // Target
      'lnm', // Target
      // Third-party affiliate params found in product URLs
      'affiliate_id',
      'clickid', // Impact Radius / Walmart
      'irgwc', // Impact Radius
      // Impact Radius, remaining set. These identify *another publisher's*
      // click, not ours — our own attribution is minted fresh by the network
      // builders in apps/api/src/lib/redirect/networks/ (impact.ts sets
      // `u` + `subId1=DUPE<shortId>DUPE` off our own deep link), so a stale
      // foreign click id on the destination URL can only muddy the retailer's
      // last-click resolution. Stripped, never preserved. See DUP-1852.
      'irclickid',
      'irpid',
      'ircid',
      'iradid',
      'irgroupname',
      'pubref', // Impact / Partnerize publisher ref
      'cjevent', // CJ
      'cjdata', // CJ
      'refid', // CJ / Wayfair
      'ranmid', // Rakuten
      'raneaid', // Rakuten
      'ransiteid', // Rakuten
      'wmlspartner', // Walmart
      'affiliates_ad_id', // Walmart
      'campaign_id', // Walmart
      'sharedid', // Walmart
      'sourceid', // Walmart
      'veh', // Walmart
      'cm_mmc', // Coremetrics / IBM
      'sscid', // ShareASale
      'cnxclid', // Connexity
      'anid', // Connexity
      'wctrck', // WeCanTrack
      'wct', // WeCanTrack
      'user_click_reference', // WeCanTrack
      // Shopify Collabs / Dovetale
      'sfdr_ptcid',
      'sfdr_hash',
      // General tracking
      'mc_cid',
      'mc_eid',
      'msclkid',
      'yclid',
      'zanpid',
      'afftrack',
      '_ga',
      '_gl',
      'vero_conv',
      'vero_id',
      'icid',
      'ncid',
      // Wayfair internal marketing
      'ireid',
      'mktdc',
      'mktcm',
      'mktst',
      'mktcr',
      'mkts',
      'fdid',
      // Frontgate / Channel Advisor
      'sourcecode',
      'cawelaid',
      'cagpspn',
      'caagid',
      'catci',
      'cmpid',
      'agid',
      'tgtid',
      'prdid',
      // Etsy PLA
      'gpla',
      'gao',
      // AliExpress campaign
      'albch',
      'albcp',
      'albag',
      'albagn',
      'albbt',
      // BBB / BedBath
      'entrytrigger',
      'exittrigger',
    ])

    // Prefix-based matching for tracking param families
    // Any param starting with these prefixes (case-insensitive) is treated as tracking
    this.trackingPrefixes = [
      'utm_', // All UTM variants (utm_id, utm_brand, utm_custom1, utm_publisher, etc.)
      'nb_', // NextBillion tracking
      'ds_e_', // DoubleClick Search
      'tw_', // Triple Whale
      'kudos_', // Kudos tracking
      'aff_', // AliExpress / generic affiliate
      'fb_', // Facebook (fb_action_ids, fb_source, etc.)
    ]

    // Site-specific rules for params to KEEP (in addition to unknown params).
    // These are explicitly preserved even if they match a tracking param.
    // Keys are base domains — the clean() method matches any subdomain
    // (e.g., 'm.wayfair.com' matches 'wayfair.com').
    this.importantParams = {
      // Amazon - product and variant identifiers
      'amazon.com': new Set(['dp', 'th', 'psc', 'smid']),
      // Wayfair - product and variant identifiers (pid = product ID, piid = variant)
      'wayfair.com': new Set(['pid', 'piid']),
      'wayfair.ca': new Set(['pid', 'piid']),
      // Note: Unknown params (variant, color, size, etc.) are kept by default
      // for all sites, so no need to list them here.
    }
  }

  clean(urlString: string) {
    const url = isValidUrl(urlString)
    if (!url) {
      return urlString
    }
    // Some queries cannot survive the URLSearchParams rebuild below, and the
    // damage is silent + unrecoverable. Return them untouched. See
    // hasFragileQuery for the two shapes and why repair isn't possible.
    if (hasFragileQuery(url.search)) {
      return urlString
    }
    const hostname = url.hostname.toLowerCase()
    // Match importantParams by base domain — e.g., 'm.wayfair.com' matches 'wayfair.com'
    const importantKey = Object.keys(this.importantParams).find(
      (k) => hostname === k || hostname.endsWith(`.${k}`)
    )
    const importantForSite =
      (importantKey ? this.importantParams[importantKey] : undefined) ??
      new Set<string>()

    // Filter params with case-insensitive comparison
    const params = new URLSearchParams()
    for (const [key, value] of url.searchParams) {
      const lowerKey = key.toLowerCase()
      // Keep if it's important for this site
      if (importantForSite.has(lowerKey)) {
        params.append(key, value)
        continue
      }
      // Remove if it's an exact match in tracking params
      if (this.trackingParams.has(lowerKey)) {
        continue
      }
      // Remove if it matches a tracking prefix
      if (this.trackingPrefixes.some((prefix) => lowerKey.startsWith(prefix))) {
        continue
      }
      // Keep unknown params by default
      params.append(key, value)
    }

    url.search = params.toString()
    return normalize(url.toString(), NORMALIZE_OPTS)
  }
}

export const urlCleaner = new URLCleaner()

/**
 * Normalize a URL for bookmark matching to handle URL variations.
 * This ensures that the same product is recognized regardless of tracking params,
 * protocol differences, www subdomain, trailing slashes, etc.
 *
 * IMPORTANT: This function preserves product-identifying parameters (variants, sizes, colors)
 * while removing only tracking parameters. This ensures different product variants are
 * treated as separate items.
 *
 * @param url The URL to normalize
 * @returns Normalized URL string, or empty string if invalid
 *
 * @example
 *   normalizeBookmarkUrl('http://shop.com/item?utm_source=google&color=blue')
 *   // returns 'https://shop.com/item?color=blue'
 *
 *   normalizeBookmarkUrl('https://www.amazon.com/item?th=1&tag=abc')
 *   // returns 'https://amazon.com/item?th=1'
 */
/**
 * Build the Redis/cache key used to store similar-products results for a research candidate.
 * Shared between API (researchController) and frontend (useReportSimilarProducts).
 */
export const buildSimilarProductsKey = (taskId: string, slug: string): string =>
  `research:${taskId}:${slug}:similar`

export const normalizeBookmarkUrl = (
  url: string | null | undefined
): string => {
  if (!url) return ''

  try {
    // Use existing validation
    const validUrl = isValidUrl(url)
    if (!validUrl) {
      return ''
    }

    // Use the URLCleaner which intelligently handles site-specific important params
    // It preserves product-identifying params while removing tracking params
    const cleanedUrl = urlCleaner.clean(url)

    return cleanedUrl
  } catch (_error) {
    // If normalization fails, return empty string
    return ''
  }
}
